
import * as React from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import type { User } from '../types';
import UsersIcon from './icons/UsersIcon';

const MyNetworkPage: React.FC = () => {
  const { currentUser, users } = useAuth();
  const { handleViewProfile, networkTab } = useNavigation();
  
  // Narrow the type to ensure it's valid for this component's local state
  const initialTab = (networkTab === 'endorsing' || networkTab === 'followers') 
    ? networkTab 
    : 'endorsing';
    
  const [activeTab, setActiveTab] = React.useState<'endorsing' | 'followers'>(initialTab);

  React.useEffect(() => {
    if (networkTab === 'endorsing' || networkTab === 'followers') {
        setActiveTab(networkTab);
    }
  }, [networkTab]);
  
  const { endorsing, followers } = React.useMemo(() => {
    if (!currentUser) return { endorsing: [], followers: [] };
    
    const userMap = new Map(users.map(u => [u.handle, u]));
    
    const endorsingList = (currentUser.endorsements || [])
      .map((handle: string) => userMap.get(handle))
      .filter((user: User | undefined): user is User => !!user);
      
    const followersList = (currentUser.endorsedBy || [])
      .map((handle: string) => userMap.get(handle))
      .filter((user: User | undefined): user is User => !!user);
      
    return { endorsing: endorsingList, followers: followersList };
  }, [currentUser, users]);
  
  if (!currentUser) {
    return null; // Or a loading state
  }

  const listToDisplay = activeTab === 'endorsing' ? endorsing : followers;

  return (
    <div className="bg-brand-surface rounded-lg p-6 md:p-8">
      <div className="flex items-center space-x-3 mb-4">
        <UsersIcon className="h-7 w-7 text-brand-orange" />
        <h2 className="text-3xl font-bold text-white">My Network</h2>
      </div>

      <div className="border-b border-gray-700">
        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
            <TabButton label="Endorsing" count={endorsing.length} isActive={activeTab === 'endorsing'} onClick={() => setActiveTab('endorsing')} />
            <TabButton label="Followers" count={followers.length} isActive={activeTab === 'followers'} onClick={() => setActiveTab('followers')} />
        </nav>
      </div>

      <div className="mt-6">
        {listToDisplay.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {listToDisplay.map((user: User) => (
              <UserCard key={user.handle} user={user} onViewProfile={() => handleViewProfile(user)} />
            ))}
          </div>
        ) : (
          <div className="text-center text-brand-muted py-12">
            <p className="font-semibold">{activeTab === 'endorsing' ? "You haven't endorsed anyone yet." : "You don't have any followers yet."}</p>
            <p className="mt-2 text-sm">{activeTab === 'endorsing' ? "Endorse other professionals to build your network." : "When someone endorses you, they'll appear here."}</p>
          </div>
        )}
      </div>
    </div>
  );
};


const TabButton: React.FC<{ label: string; count: number; isActive: boolean; onClick: () => void }> = ({ label, count, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
            isActive
            ? 'border-brand-orange text-brand-orange'
            : 'border-transparent text-brand-muted hover:text-white hover:border-gray-500'
        }`}
    >
        {label} <span className="bg-gray-700/80 text-xs font-bold px-2 py-0.5 rounded-full">{count}</span>
    </button>
);

const UserCard: React.FC<{ user: User, onViewProfile: () => void }> = ({ user, onViewProfile }) => (
    <div 
      key={user.handle} 
      className="bg-gray-800/50 rounded-lg p-4 text-center border-2 border-transparent hover:border-brand-orange transition-all duration-300 cursor-pointer"
      onClick={onViewProfile}
    >
      <img src={user.avatarUrl} alt={user.name} className="h-24 w-24 rounded-full mx-auto" />
      <h3 className="mt-4 text-lg font-semibold text-white truncate">{user.name}</h3>
      <p className="text-sm text-brand-muted">@{user.handle}</p>
      <p className="mt-2 text-xs text-gray-400 line-clamp-2">{user.title}</p>
    </div>
);


export default MyNetworkPage;
